import * as _ from 'lodash';
import { matchValue } from '../utilities/parts';
import parse, { domToReact } from 'html-react-parser';
import React from 'react';

/**
 * 郵便番号にハイフンを入れる
 *
 * @param {string} postalCode 元の郵便番号
 * @returns 返還後の郵便番号
 */
export const convertPostalCode = postalCode => {
  let res = postalCode;
  // 郵便番号のハイフンがない時だけ
  if (_.indexOf(postalCode, '-') === -1) {
    res = insert(postalCode, 3, '-');
  }

  return res;
};

/**
 * 文字列にindex指定で文字を挿入する
 *
 * @param {string} str 元の文字数
 * @param {number} idx インデックス
 * @param {string} val 挿入したい文字
 * @returns 挿入後の文字列
 */
export const insert = (str, idx, val) => {
  const res = `${str.slice(0, idx)}${val}${str.slice(idx)}`;
  return res;
};

/**
 * urlなどの先頭文字が一致した場合に置き換える関数
 * 先頭が /share/ の場合に API.nfs のアドレスで置き換えるときに
 * 使用される場面が多いために作成した
 */
export const headReplace = (str, head, replace) => {
  if (_.isNil(str)) {
    return '';
  }
  if (str.startsWith(head)) {
    return str.replace(head, replace);
  } else {
    return str;
  }
};

/**
 * メッセージのリプレースメント
 *
 * @param {*} message {0}{1}を含む元のメッセージ
 * @param {*} args {0}{1}に対するリプレースメントを可変長引数
 * @returns
 */
export const replacementMessage = (message, ...args) => {
  // リプレースメント {数字}
  // メッセージ内のリプレースメント確認
  // 「g」… 一致するすべての文字を検索、指定なしの場合は最初の1つだけ検索。
  let matched = message.match(/{\d*}/g);

  // メッセージ内の置き換え文字をユニークにする
  const uniq = _.uniq(matched);

  // リプレースメントが含まれたとき
  if (matched !== null && matched.length > 0) {
    if (_.isNil(args)) {
      // リプレースメントのメッセージなのに置き換え文字をパラメータとして渡してない
      throw new Error('no replace argumens');
    } else if (uniq.length !== args.length) {
      // リプレースメントのメッセージと置き換え文字パラメータの数が合ってない
      throw new Error('invalid replace argumens length');
    } else {
      // 並び替え
      const sorted = _.sortBy(uniq, num => {
        // 「g」… 一致するすべての文字を検索、指定なしの場合は最初の1つだけ検索。
        return _.parseInt(num.replace(/['{'|'}']/g, ''));
      });

      // 引数のものに順次置き換え
      _.map(sorted, (item, index) => {
        // {0}のカッコを外してindexのチェック
        const parsedInt = _.parseInt(item.replace(/['{'|'}']/g, ''));
        if (parsedInt !== index) {
          throw new Error('invalid replace argumens format');
        }

        // {0}を単純にRegExp化できないので
        // 閉じカッコは}の前にエスケープ
        const tmp = item.replace(item.replace('}', `\\}`));
        // 開始かっこは{の前にエスケープで正規表現化
        const pattern = new RegExp(`\\${tmp}`, 'g');

        // 複数なので正規表現を第1引数
        message = message.replace(pattern, args[index]);
      });
    }
  }

  return message;
};

/**
 * '1' '0' の文字列を〇か×に変換する
 * Buildee利用のjoining_flagを文字に変換などに使用する
 *
 * @param {*} flagString 1' '0' の文字列
 * @returns 〇か×
 */
export const convertFlagToCircleOrCross = flagString => {
  flagString = _.defaultTo(flagString, '0');
  switch (flagString) {
    // stringになっている事に注意
    case '1':
      return '〇';
    case '0':
      return '×';
    default:
      // 保険
      return '×';
  }
};

/** カタカナをひらがなに変更する
 * @param {string} text
 */
export const convertHiragana = text => {
  return `${text}`.replace(/[ァ-ヴ]/g, function(string) {
    return String.fromCharCode(string.charCodeAt(0) - 0x60);
  });
};

/**
 * Null, undefined, 空文字を判別する
 * 注）対象がオジェクトの可能性があるなら軒並みfalseを返します
 *    要望が多ければオジェクトも含めて判別するようにしたいと思います。
 * @param {プリミティブな値} value
 * @returns true: Nullかundefinedか空文字、false: それ以外
 *  2020/04/09(Thu) @sa-yamamoto
 *  非推奨です
 *  https://e-reverse.esa.io/posts/316#isNullOrEmptyの命名の問題
 *  中身同じで関数名が違う isNullOrUndefinedOrEmptyStr を作ったので利用してください。
 */
export const isNullOrEmpty = value => {
  return value === null || value === undefined || value === '';
};

/**
 * Null undefined を判別する
 */
export const isNullOrUndefined = value => {
  return value === null || value === undefined;
};

/**
 * Null, undefined, 空文字を判別する
 */
export const isNullOrUndefinedOrEmptyStr = value => {
  return value === null || value === undefined || value === '';
};

/**
 * stringToNumber
 *  parseInt/parseFloat/Numberでは余計なものも数値に変換してしまうので
 *  String型限定で変換できるものだけ数値に変換し
 *  変換できない場合は例外を返す関数
 */
export const stringToNumber = value => {
  if (!_.isString(value)) {
    throw new TypeError('stringToNumber args(value) is not string');
  }
  if (value.trim() === '') {
    throw new RangeError('stringToNumber args(value) is empty string');
  }
  const result = Number(value);
  if (Number.isNaN(result)) {
    throw new RangeError(
      'stringToNumber args(value) is not able to change number'
    );
  }
  return result;
};

export const stringToNumberDefault = (value, defaultValue = NaN) => {
  if (!_.isString(value)) {
    return defaultValue;
  }
  if (value.trim() === '') {
    return defaultValue;
  }
  const result = Number(value);
  if (Number.isNaN(result)) {
    return defaultValue;
  }
  return result;
};

/**
 * 制御文字の削除
 */
export const deleteControlCode = value => {
  return matchValue(value, _.isString, value =>
    value.replace(/[\x00-\x09\x0b-\x1f\x7f-\x9f]/g, '')
  );
};

export const deleteControlCodeFromEvent = event => {
  //iosの場合にtarget.valueを書き換えると入力が繰り返されるような挙動になるので何もしない
  //制御コードは入力できないはず
  if (!iosCheck()) {
    event.target.value = deleteControlCode(event.target.value);
  }
};

export const iosCheck = () => {
  var ua = navigator.userAgent.toLowerCase();
  //iOS
  if (
    (ua.indexOf('ipod') >= 0 ||
      ua.indexOf('iphone') >= 0 ||
      ua.indexOf('ipad') >= 0 ||
      ua.indexOf('macintosh') >= 0) &&
    'ontouchend' in document
  ) {
    return true;
  }
  return false;
};

export const safariOnIosCheck = () => {
  var ua = navigator.userAgent.toLowerCase();
  //iOS
  if (iosCheck() && ua.indexOf('safari') >= 0) {
    return true;
  }
  return false;
};

export const getiOSVersion = () => {
  if (!iosCheck()) return null;

  const userAgent = navigator.userAgent;

  // iOSのChrome（デスクトップモード）でバージョン判定不能の為、除外
  if(!userAgent.includes("CriOS")) {
    const versionMatch = userAgent.match(/Version\/(\d+)\.(\d+)/);
    if (versionMatch) {
      return parseInt(versionMatch[1], 10);
    }
  }

  // Chrome（デスクトップを除いて）などの場合
  const cpuOSMatch = userAgent.match(/CPU (?:iPhone )?OS (\d+)_\d+(_\d+)? like Mac OS X/i); // "CPU OS"または、"CPU iPhone OS"にマッチ
  if (cpuOSMatch) {
    return parseInt(cpuOSMatch[1], 10);
  }

  // OS検知できない場合、nullを返す
  return null;
};

/**
 * 文字列置き換え
 */
export const replaceAll = (str, substr, newSubstr) => {
  return str.split(substr).join(newSubstr);
};

export const joinTel = (tel1, tel2, tel3) => {
  return formatTel(`${tel1 || ''}-${tel2 || ''}-${tel3 || ''}`);
};

export const formatTel = tel => {
  if (tel === '--' || _.isNil(tel)) {
    return '';
  }

  return tel;
};

export const hyphenToFowardSlash = str =>
  _.isString(str) ? str.replace(/-/g, '/') : '';

export const maskTel = string => {
  if (_.isNull(string)) {
    return string;
  } else {
    const masked = `${string.slice(0, -4)}****`;
    return masked;
  }
};

/**
 * 変換したCCUSエラーメッセージ中の<erc-link>タグを<a>タグに変換する
 * @param {node} string: 入力文字列
 * */
export const replaceTagInErrorMessage = node => {
  return parse(node, {
    replace(node) {
      if (node.type === 'tag') {
        if (node.name === 'erc-link') {
          return (
            <a
              {...node.attribs}
              className='clickable-text'
              target='_blank'
              rel='noopener noreferrer'
            >
              {domToReact(node.children)}
            </a>
          );
        } else {
          //変換対象外のタグは無効化する。
          return <div />;
        }
      }
    },
  });
};

/**
 * trim and remove line break
 * @param {string} text
 * @returns {string}
 */
export const trimAndRemoveLineBreak = text => {
  const lineBreakRegx = /\r?\n|\r/;
  return replaceAll(_.trim(text), lineBreakRegx, '');
};



// WEBPACK FOOTER //
// ./src/utilities/stringUtil.js